/**
 * Time-series bucket normalization for dashboard charts.
 *
 * Bucket key formats mirror backend statistics API grouping (UTC):
 * - lastHour: HH:MI (5-minute buckets)
 * - today/yesterday/custom (short): HH:00 (hourly)
 * - day periods: YYYY-MM-DD
 */

import { resolveChartGranularity } from './timeRange';

const BOUNDED_PERIODS = new Set([
  'lastHour',
  'today',
  'yesterday',
  'last7days',
  'last30days',
  'last90days',
  'thisMonth',
]);

const PERIOD_DAY_LOOKBACK = {
  last7days: 7,
  last30days: 30,
  last90days: 90,
};

const REQUESTS_BUCKET_DEFAULTS = {
  api_successful: 0,
  api_failed: 0,
  direct_successful: 0,
  direct_failed: 0,
  bot_successful: 0,
  bot_failed: 0,
  widget_successful: 0,
  widget_failed: 0,
  successful: 0,
  failed: 0,
  direct_requests: 0,
};

const TOKEN_BUCKET_DEFAULTS = {
  platform_tokens: 0,
  api_tokens: 0,
  bot_tokens: 0,
  widget_tokens: 0,
  tokens: 0,
};

const CLOUD_SPEND_BUCKET_DEFAULTS = {
  platform_cost_usd: 0,
  api_cost_usd: 0,
  bot_cost_usd: 0,
  widget_cost_usd: 0,
  total_cost_usd: 0,
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatUtcDate(date) {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

function resolveTimeRange(input) {
  if (typeof input === 'string') return { period: input };
  return input || { period: 'last7days' };
}

function getUtcDayRange(period) {
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  if (period === 'today') {
    return [end];
  }

  if (period === 'yesterday') {
    const day = new Date(end);
    day.setUTCDate(day.getUTCDate() - 1);
    return [day];
  }

  let start;
  if (period in PERIOD_DAY_LOOKBACK) {
    start = new Date(end);
    start.setUTCDate(start.getUTCDate() - PERIOD_DAY_LOOKBACK[period]);
  } else if (period === 'thisMonth') {
    start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  } else {
    return [];
  }

  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(new Date(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

function getLastHourBucketKeys() {
  const now = new Date();
  const keys = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getTime() - i * 5 * 60 * 1000);
    const minutes = Math.floor(d.getUTCMinutes() / 5) * 5;
    keys.push(`${pad2(d.getUTCHours())}:${pad2(minutes)}`);
  }
  return keys;
}

function getHourlyBucketKeysForDay(dayDate) {
  return Array.from({ length: 24 }, (_, hour) => `${pad2(hour)}:00`);
}

function getCustomBucketKeys(timeRange) {
  const granularity = resolveChartGranularity(timeRange);
  const start = new Date(timeRange.startDate);
  const end = new Date(timeRange.endDate);
  const keys = [];

  if (granularity === 'hour') {
    const cursor = new Date(start);
    cursor.setUTCMinutes(0, 0, 0);
    while (cursor <= end) {
      keys.push(`${pad2(cursor.getUTCHours())}:${pad2(cursor.getUTCMinutes())}`);
      cursor.setUTCHours(cursor.getUTCHours() + 1);
    }
    return keys;
  }

  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (cursor <= endDay) {
    keys.push(formatUtcDate(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return keys;
}

export function normalizeBucketKey(timeRangeInput, time) {
  const timeRange = resolveTimeRange(timeRangeInput);
  const period = timeRange.period;
  if (time == null) return '';
  const value = String(time).trim();
  if (!value) return '';

  const granularity = resolveChartGranularity(timeRange);
  if (granularity === 'five_minute' || granularity === 'hour') {
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      return `${pad2(Number(match[1]))}:${match[2]}`;
    }
    return value;
  }

  const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return dateMatch ? dateMatch[1] : value;
}

function compareBucketKeys(a, b) {
  return a.localeCompare(b);
}

export function isBoundedPeriod(period) {
  return BOUNDED_PERIODS.has(period);
}

export function isBoundedTimeRange(timeRangeInput) {
  const timeRange = resolveTimeRange(timeRangeInput);
  const { period } = timeRange;
  if (period === 'allTime') return false;
  if (period === 'custom') return !!(timeRange.startDate && timeRange.endDate);
  return BOUNDED_PERIODS.has(period);
}

export function getPeriodBucketKeys(timeRangeInput) {
  const timeRange = resolveTimeRange(timeRangeInput);
  const { period } = timeRange;

  if (period === 'lastHour') {
    return getLastHourBucketKeys();
  }

  if (period === 'today' || period === 'yesterday') {
    const now = new Date();
    const day = period === 'today'
      ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      : (() => {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        d.setUTCDate(d.getUTCDate() - 1);
        return d;
      })();
    return getHourlyBucketKeysForDay(day);
  }

  if (period === 'custom') {
    return getCustomBucketKeys(timeRange);
  }

  if (!isBoundedPeriod(period) || period === 'allTime') {
    return [];
  }

  return getUtcDayRange(period).map(formatUtcDate);
}

export function createRequestsBucket(time) {
  return {
    time,
    ...REQUESTS_BUCKET_DEFAULTS,
  };
}

export function createTokenBucket(time) {
  return {
    time,
    ...TOKEN_BUCKET_DEFAULTS,
  };
}

export function createCloudSpendBucket(time) {
  return {
    time,
    ...CLOUD_SPEND_BUCKET_DEFAULTS,
  };
}

export const REQUESTS_CHART_SERIES = [
  { key: 'api_failed', label: 'API failed', color: '#ef4444' },
  { key: 'direct_failed', label: 'Chat failed', color: '#f59e0b' },
  { key: 'widget_failed', label: 'Widgets failed', color: '#ec4899' },
  { key: 'bot_failed', label: 'Bots failed', color: '#a855f7' },
  { key: 'api_successful', label: 'API successful', color: '#10b981' },
  { key: 'direct_successful', label: 'Chat successful', color: '#3b82f6' },
  { key: 'widget_successful', label: 'Widgets successful', color: '#f472b6' },
  { key: 'bot_successful', label: 'Bots answered', color: '#8b5cf6' },
];

export const TOKEN_CHART_SERIES = [
  { key: 'platform_tokens', label: 'Chat', color: '#3b82f6' },
  { key: 'api_tokens', label: 'API', color: '#10b981' },
  { key: 'bot_tokens', label: 'Bots', color: '#8b5cf6' },
  { key: 'widget_tokens', label: 'Widgets', color: '#ec4899' },
];

export const CLOUD_SPEND_CHART_SERIES = [
  { key: 'api_cost_usd', label: 'API', color: '#10b981' },
  { key: 'platform_cost_usd', label: 'Chat', color: '#3b82f6' },
  { key: 'widget_cost_usd', label: 'Widgets', color: '#ec4899' },
  { key: 'bot_cost_usd', label: 'Bots', color: '#8b5cf6' },
];

export const REQUESTS_TIME_SERIES = {
  valueKeys: [
    'api_successful',
    'api_failed',
    'direct_successful',
    'direct_failed',
    'bot_successful',
    'bot_failed',
    'widget_successful',
    'widget_failed',
    'successful',
    'failed',
  ],
  createBucket: createRequestsBucket,
};

export const TOKEN_TIME_SERIES = {
  valueKeys: [
    'platform_tokens',
    'api_tokens',
    'bot_tokens',
    'widget_tokens',
    'tokens',
  ],
  createBucket: createTokenBucket,
};

export const CLOUD_SPEND_TIME_SERIES = {
  valueKeys: [
    'platform_cost_usd',
    'api_cost_usd',
    'bot_cost_usd',
    'widget_cost_usd',
    'total_cost_usd',
  ],
  createBucket: createCloudSpendBucket,
};

export function filterActiveSeries(chartData, series) {
  return series.filter(({ key }) =>
    chartData.some((row) => Number(row?.[key] ?? 0) > 0)
  );
}

export function getSeriesEntriesAtPoint(payload, activeSeries) {
  if (!Array.isArray(payload) || !Array.isArray(activeSeries)) {
    return [];
  }

  return activeSeries.map(({ key, label, color }) => ({
    key,
    label,
    color,
    value: Number(payload.find((entry) => entry.dataKey === key)?.value ?? 0),
  }));
}

export function getNonZeroSeriesEntriesAtPoint(payload, activeSeries) {
  return getSeriesEntriesAtPoint(payload, activeSeries).filter((entry) => entry.value > 0);
}

export function sumSeriesEntries(entries) {
  return entries.reduce((sum, entry) => sum + entry.value, 0);
}

export const STACKED_AREA_STROKE = 'none';

export function buildTimeSeriesSkeleton(timeRangeInput, createBucket) {
  return getPeriodBucketKeys(timeRangeInput).map((time) => createBucket(time));
}

export function mergeTimeSeriesData(timeRangeInput, rawData, createBucket) {
  const rows = Array.isArray(rawData) ? rawData : [];

  if (!isBoundedTimeRange(timeRangeInput)) {
    return rows;
  }

  const byTime = new Map(
    buildTimeSeriesSkeleton(timeRangeInput, createBucket).map((bucket) => [bucket.time, { ...bucket }])
  );

  for (const row of rows) {
    const time = normalizeBucketKey(timeRangeInput, row?.time);
    if (!time) continue;

    const base = byTime.get(time) ?? createBucket(time);
    byTime.set(time, {
      ...base,
      ...row,
      time,
    });
  }

  return Array.from(byTime.values()).sort((left, right) =>
    compareBucketKeys(left.time, right.time)
  );
}
